import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, onSnapshot, deleteDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { collectionPath } from '../utils/firestoreCollections';
import Toast from '../components/common/Toast';
import SegmentBuilder from '../components/forms/SegmentBuilder';
import { useAuth } from '../components/auth/AuthContext';
import { FEATURE_PERMISSIONS } from '../utils/permissions';
import { usePermissions } from '../hooks/usePermissions';
import { logUserAction } from '../utils/logger';
import { canViewAllEmail } from '../utils/emailAccess';

const EmailSegmentsPage = () => {
  const { currentUser } = useAuth();
  const { can } = usePermissions(currentUser);
  const viewAllEmail = canViewAllEmail(currentUser, can);
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editSegment, setEditSegment] = useState(null);
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // 'active' or 'archived'

  useEffect(() => {
    let active = true;
    setSegments([]);
    setLoading(true);
    const source = collection(db, collectionPath('emailSegments'));
    const unsub = onSnapshot(
      viewAllEmail ? source : query(source, where('createdBy', '==', currentUser.uid)),
      (snap) => {
        if (!active) return;
        setSegments(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a, b) =>
          (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)));
        setLoading(false);
      },
      (error) => {
        if (!active) return;
        console.error('Failed to load segments:', error);
        setToast({ message: `Could not load segments: ${error.message}`, type: 'error' });
        setLoading(false);
      }
    );
    return () => { active = false; unsub(); };
  }, [currentUser.uid, viewAllEmail]);

  const handleArchiveToggle = async (segment) => {
    const willArchive = !segment.isArchived;
    try {
      await updateDoc(doc(db, collectionPath('emailSegments'), segment.id), {
        isArchived: willArchive,
        archivedAt: willArchive ? serverTimestamp() : null,
        archivedByName: willArchive ? (currentUser?.displayName || currentUser?.email || 'Unknown') : null
      });
      await logUserAction({
        action: willArchive ? 'Email Segment Archived' : 'Email Segment Restored',
        details: `Segment ${willArchive ? 'archived' : 'restored'}: "${segment.name}"`,
        entityType: 'EmailSegment',
        entityId: segment.id
      });
      setToast({ message: willArchive ? 'Segment archived' : 'Segment restored', type: 'success' });
    } catch (error) {
      console.error('Archive toggle failed:', error);
      setToast({ message: 'Failed to update segment', type: 'error' });
    }
  };

  const handleDelete = async (segment) => {
    if (!segment.isArchived) {
      setToast({ message: 'Archive the segment before deleting it', type: 'error' });
      return;
    }
    if (!window.confirm(`Permanently delete archived segment "${segment.name}"? This cannot be undone and may affect historical campaign analytics.`)) return;

    try {
      await deleteDoc(doc(db, collectionPath('emailSegments'), segment.id));
      await logUserAction({ action: 'Email Segment Deleted', details: `Segment deleted: "${segment.name}"`, entityType: 'EmailSegment', entityId: segment.id });
      setToast({ message: 'Segment deleted', type: 'success' });
    } catch (error) {
      setToast({ message: 'Failed to delete segment', type: 'error' });
    }
  };

  // Filter segments based on search term and archive status
  const filteredSegments = useMemo(() => segments.filter(segment => {
    if (statusFilter === 'active' && segment.isArchived) return false;
    if (statusFilter === 'archived' && !segment.isArchived) return false;

    if (!search.trim()) return true;
    const searchLower = search.toLowerCase();
    return (
      segment.name?.toLowerCase().includes(searchLower) ||
      segment.description?.toLowerCase().includes(searchLower)
    );
  }), [segments, search, statusFilter]);

  const activeCount = useMemo(() => segments.filter(s => !s.isArchived).length, [segments]);
  const archivedCount = useMemo(() => segments.filter(s => s.isArchived).length, [segments]);

  if (loading) {
    return (
      <main className="flex-grow p-6 overflow-y-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading segments...</div>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-grow p-6 overflow-y-auto">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Audience Segments</h2>
          <p className="text-sm text-gray-500">{viewAllEmail ? 'Tenant-wide audiences' : 'Your segments, limited to your currently assigned customers and contacts.'}</p>
        </div>
        {can(FEATURE_PERMISSIONS.EMAIL_CREATE_SEGMENT) && (
        <button
          onClick={() => {
            setEditSegment(null);
            setShowBuilder(true);
          }}
          className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
        >
          Create Segment
        </button>
        )}
      </div>

      {/* Search Input + Status Filter */}
      <div className="mb-4 flex flex-col sm:flex-row gap-3 sm:items-center">
        <input
          type="text"
          placeholder="Search segments by name or description..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={`flex-1 px-4 py-2 border rounded-lg transition-colors duration-150 ${
            search ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
          }`}
        />
        <div className="flex border border-gray-300 rounded-lg overflow-hidden bg-white">
          <button
            type="button"
            onClick={() => setStatusFilter('active')}
            className={`px-4 py-2 text-sm font-medium ${statusFilter === 'active' ? 'bg-indigo-600 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
          >
            Active ({activeCount})
          </button>
          <button
            type="button"
            onClick={() => setStatusFilter('archived')}
            className={`px-4 py-2 text-sm font-medium border-l border-gray-300 ${statusFilter === 'archived' ? 'bg-indigo-600 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
          >
            Archived ({archivedCount})
          </button>
        </div>
      </div>
      {search && (
        <div className="mb-3 text-sm text-gray-600">
          Found {filteredSegments.length} segment{filteredSegments.length === 1 ? '' : 's'}
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recipients</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Conditions</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created By</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Updated By</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Updated</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredSegments.length === 0 ? (
              <tr>
                <td colSpan="9" className="px-6 py-8 text-center text-gray-500">
                  {segments.length === 0
                    ? 'No segments created yet. Create your first segment to organize your audience.'
                    : statusFilter === 'archived'
                      ? 'No archived segments.'
                      : 'No segments match your search criteria.'}
                </td>
              </tr>
            ) : (
              filteredSegments.map(segment => (
                <tr key={segment.id} className={`hover:bg-gray-50 ${segment.isArchived ? 'bg-amber-50' : ''}`}>
                  <td className="px-6 py-4 font-medium text-gray-900">
                    {segment.name}
                    {segment.isArchived && (
                      <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-800">Archived</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{segment.description}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {segment.contactCount?.toLocaleString() || 0}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {segment.rules?.conditions?.length || 0} condition(s)
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {segment.createdByName || 'Unknown'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {segment.createdAt ? new Date(segment.createdAt.toDate()).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {segment.updatedByName || segment.createdByName || 'Unknown'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {segment.updatedAt ? new Date(segment.updatedAt.toDate()).toLocaleDateString() : segment.createdAt ? new Date(segment.createdAt.toDate()).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {can(FEATURE_PERMISSIONS.EMAIL_EDIT_SEGMENT) && (
                    <>
                    <button
                      onClick={() => {
                        setEditSegment(segment);
                        setShowBuilder(true);
                      }}
                      className="text-indigo-600 hover:text-indigo-900 mr-3"
                    >
                      {segment.isArchived ? 'View' : 'Edit'}
                    </button>
                    <button
                      onClick={() => handleArchiveToggle(segment)}
                      className={`mr-3 ${segment.isArchived ? 'text-green-600 hover:text-green-900' : 'text-amber-600 hover:text-amber-900'}`}
                      title={segment.isArchived ? 'Restore segment' : 'Archive segment (preserves analytics for past campaigns)'}
                    >
                      {segment.isArchived ? 'Restore' : 'Archive'}
                    </button>
                    {can(FEATURE_PERMISSIONS.EMAIL_DELETE_SEGMENT) && segment.isArchived && (
                    <button
                      onClick={() => handleDelete(segment)}
                      className="text-red-600 hover:text-red-900"
                      title="Permanently delete (only allowed for archived segments)"
                    >
                      Delete
                    </button>
                    )}
                    </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showBuilder && (
        <SegmentBuilder
          segment={editSegment}
          onClose={() => {
            setShowBuilder(false);
            setEditSegment(null);
          }}
          onSaved={(message) => {
            setShowBuilder(false);
            setEditSegment(null);
            setToast({ message, type: 'success' });
          }}
        />
      )}
    </main>
  );
};

export default EmailSegmentsPage;